import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaProjectDiagram } from 'react-icons/fa';
import { RiHandCoinLine } from 'react-icons/ri';
import { PropagateLoader } from 'react-spinners';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

import AddRowButton from '~/components/Buttons/AddRow';
import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DeleteRowButton from '~/components/Buttons/DeleteRow';
import ExitButton from '~/components/Buttons/Exit';
import NoticeDialog from '~/components/Dialogs/Notice';
import ProjectInfo from '~/components/ProjectInfo/Default';
import SaveButton from '~/components/Buttons/Save';
import TableCurrencyInput from '~/components/TableInputs/Currency';
import TableDateInput from '~/components/TableInputs/Date';
import TableDefaultInput from '~/components/TableInputs/Default';
import TextArea from '~/components/TextArea';
import UploadFiles from '~/components/UploadFiles';
import ValidateAltButton from '~/components/Buttons/ValidateAlt';

import { getCurrencyFormatValue, getFormattedCurrency } from '~/utils';

import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Content, Wrapper, Label, Error } from './styles';

interface IFormProcedure5 {
  record_note: string;
}

interface ILocation {
  id: string;
  type: string;
  code: string;
  price: number;
  status: string;
  project_procedure_id: string;
}

interface IInvoice {
  id?: string;
  issue_date: Date;
  series: string;
  number: string;
  gross_price: number;
  discount_price: number;
  title_series: string;
  title_number: string;
  title_installment: string;
  sequence: number;
  project_id: string;
  user_inclusion_id: string;
  user_update_id: string;
  previous: boolean;
  deleted: boolean;
  issue_date_error: string;
  internal_invoice_error: string;
  external_invoice_error: string;
  empty_fields_error: string;
  price_error: string;
  validation_status: string;
}

const Procedure5: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const projectTemp = useRef(uuidv4());
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const lastSequence = useRef(1);
  const totalDiscountPrice = useRef(0);
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenNotice, setIsOpenNotice] = useState(false);
  const [notice, setNotice] = useState<{
    title: string;
    subtitle: string;
  }>({
    title: '',
    subtitle: '',
  });

  const { handleSubmit, register, errors } = useForm<IFormProcedure5>();

  useEffect(() => {
    if (state === undefined) {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Solicitação não localizada',
      });

      history.push('/projects/rebate');
    } else if (state.status.toUpperCase() === 'CANCELADO') {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Solicitação Cancelada',
      });

      history.push('/projects/rebate');
    } else if (state.status.toUpperCase() === 'FINALIZADO') {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Solicitação Finalizada',
      });

      history.push('/projects/rebate');
    } else {
      api
        .get(`/projectinvoices/projectall?project_id=${state.id}`)
        .then(response => {
          if (response.data.length > 0) {
            setInvoices(
              response.data.map(
                (p_i: {
                  id: string;
                  number: string;
                  series: string;
                  issue_date: Date;
                  gross_price: number;
                  discount_price: number;
                  sequence: number;
                  title_series: string;
                  title_number: string;
                  title_installment: string;
                  project_id: string;
                  user_inclusion_id: string;
                  user_update_id: string;
                }) => ({
                  id: p_i.id,
                  issue_date: new Date(
                    new Date(p_i.issue_date).setHours(0, 0, 0, 0),
                  ),
                  series: p_i.series,
                  number: p_i.number,
                  gross_price: p_i.gross_price,
                  discount_price: p_i.discount_price,
                  title_series: p_i.title_series,
                  title_number: p_i.title_number,
                  title_installment: p_i.title_installment,
                  sequence: p_i.sequence,
                  project_id: p_i.project_id,
                  user_inclusion_id: p_i.user_inclusion_id,
                  user_update_id: p_i.user_update_id,
                  previous: true,
                  deleted: false,
                  issue_date_error: '',
                  internal_invoice_error: '',
                  external_invoice_error: '',
                  empty_fields_error: '',
                  price_error: '',
                  validation_status: 'success',
                }),
              ),
            );

            lastSequence.current =
              response.data[response.data.length - 1].sequence;
          } else {
            setInvoices([
              {
                issue_date: new Date(new Date().setHours(0, 0, 0, 0)),
                series: '',
                number: '',
                gross_price: 0,
                discount_price: 0,
                title_series: '',
                title_number: '',
                title_installment: '',
                sequence: 1,
                project_id: state.id,
                user_inclusion_id: user.id,
                user_update_id: user.id,
                previous: false,
                deleted: false,
                issue_date_error: '',
                internal_invoice_error: '',
                external_invoice_error: '',
                empty_fields_error: '',
                price_error: '',
                validation_status: 'pending',
              },
            ]);
          }
        });
    }
  }, [addToast, history, state, user]);

  const validateInvoice = async (invoice: IInvoice) => {
    let issue_date_error = '';
    let internal_invoice_error = '';
    let external_invoice_error = '';
    let empty_fields_error = '';
    let price_error = '';

    if (!moment(invoice.issue_date).isValid()) {
      issue_date_error = 'Data Inválida.';
    }

    if (
      invoice.series === '' ||
      invoice.number === '' ||
      invoice.title_series === '' ||
      invoice.title_number === '' ||
      invoice.title_installment === ''
    ) {
      empty_fields_error = 'Existem campos não preenchidos.';
    }

    if (issue_date_error === '' && empty_fields_error === '') {
      const findInternalConflicts = invoices.filter(
        i =>
          i.sequence !== invoice.sequence &&
          !i.deleted &&
          i.series === invoice.series &&
          i.number === invoice.number &&
          i.title_series === invoice.title_series &&
          i.title_number === invoice.title_number &&
          i.title_installment === invoice.title_installment,
      );

      internal_invoice_error =
        findInternalConflicts.length > 0 ? 'Título já informado.' : '';

      await api
        .get(
          `/projectinvoices/validinvoice?` +
            `series=${invoice.series}&` +
            `number=${invoice.number}&` +
            `issue_date=${invoice.issue_date}&` +
            `title_series=${invoice.title_series}&` +
            `title_number=${invoice.title_number}&` +
            `title_installment=${invoice.title_installment}`,
        )
        .then(response => {
          if (
            response.data.project_id &&
            response.data.project_id !== state.id
          ) {
            external_invoice_error =
              'Título já utilizado em outra solicitação.';
          }
        });
    }

    if (invoice.gross_price <= 0 || invoice.discount_price <= 0) {
      price_error = 'Valores devem ser maiores que zero.';
    } else if (invoice.discount_price > invoice.gross_price) {
      price_error = 'Valor do desconto não pode ser maior que o valor da nota.';
    } else if (invoice.discount_price > state.price) {
      price_error =
        'Valor do desconto não pode ser maior que o valor da solicitação.';
    }

    setInvoices(prevInvoices =>
      prevInvoices.map(prevInv =>
        prevInv.sequence === invoice.sequence
          ? {
              ...prevInv,
              issue_date_error,
              internal_invoice_error,
              external_invoice_error,
              empty_fields_error,
              price_error,
              validation_status:
                issue_date_error === '' &&
                internal_invoice_error === '' &&
                external_invoice_error === '' &&
                empty_fields_error === '' &&
                price_error === ''
                  ? 'success'
                  : 'error',
            }
          : { ...prevInv },
      ),
    );
  };

  const submitForm = useCallback(
    async (data: IFormProcedure5) => {
      try {
        setIsSaving(true);

        await api.put(`/projects/${state.id}`, {
          ...data,
          code: state.code,
          project_invoices: invoices,
          project_procedure_id: state.project_procedure_id,
          project_temp_id: projectTemp.current,
          record_description:
            totalDiscountPrice.current >= state.price
              ? `Finalização da solicitação no valor de ` +
                `${getFormattedCurrency(totalDiscountPrice.current)}`
              : `Atualização da solicitação no valor de ` +
                `${getFormattedCurrency(totalDiscountPrice.current)}`,
          type: 'REBATE',
          validation_type: 'APROVADO',
        });

        addToast({
          type: 'success',
          title: 'Solicitação atualizada!',
          description: 'Solicitação atualizada com sucesso.',
        });

        history.push('/projects/rebate');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, invoices, projectTemp, state, totalDiscountPrice],
  );

  const onExit = () => {
    api.delete(`projectfilesreceived/projecttempid/${projectTemp.current}`);

    history.push('/projects/rebate');
  };

  return (
    <Container>
      <BreadCrumb
        main="Solicitações"
        path="/projects/main"
        firstChildPath="/projects/rebate"
        firstChild="Rebate"
        secondChild="Atualização Solicitação"
        mainIcon={FaProjectDiagram}
        firstChildIcon={RiHandCoinLine}
        secondChildIcon={RiHandCoinLine}
        mainColored
        firstChildColored
      />

      <ProjectInfo project_id={state.id} />

      <form onSubmit={handleSubmit(submitForm)}>
        <div style={{ marginTop: 20 }}>
          <Label>Notas Fiscais</Label>
        </div>

        {invoices.length > 0 ? (
          <div style={{ maxWidth: 680 }}>
            {invoices
              .filter(i => !i.deleted)
              .map(invoice => (
                <div
                  key={invoice.sequence}
                  style={{
                    borderRadius: '0.4rem',
                    border: '1px solid #e4e4e4',
                    padding: '1rem',
                  }}
                >
                  <div
                    style={{
                      margin: '0 0 1rem 0',
                      fontSize: 14,
                      fontWeight: 'bold',
                    }}
                  >
                    {`SEQUÊNCIA: ${invoice.sequence}`}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <Wrapper style={{ width: 200 }}>
                      <TableDateInput
                        type="text"
                        labelText="Data Emissão"
                        upperCaseLabel
                        defaultValue={invoice.issue_date.toLocaleDateString()}
                        disabled={invoice.previous}
                        onChange={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInvoice => {
                              if (prevInvoice.sequence === invoice.sequence) {
                                const issueDate = moment(
                                  e.target.value,
                                  'DD/MM/YYYY',
                                ).toDate();

                                return {
                                  ...prevInvoice,
                                  issue_date: issueDate,
                                  issue_date_error:
                                    !moment(
                                      e.target.value,
                                      'DD/MM/YYYY',
                                    ).isValid() ||
                                    issueDate.setHours(0, 0, 0, 0) >
                                      new Date().setHours(0, 0, 0, 0)
                                      ? 'Data Inválida'
                                      : '',
                                  validation_status: 'pending',
                                };
                              }

                              return { ...prevInvoice };
                            }),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableDefaultInput
                        type="text"
                        labelText="Série NF"
                        upperCaseLabel
                        defaultValue={invoice.series}
                        maxLength={3}
                        disabled={invoice.previous}
                        onChange={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    series: e.target.value,
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableDefaultInput
                        type="text"
                        labelText="Número NF"
                        upperCaseLabel
                        defaultValue={invoice.number}
                        maxLength={7}
                        disabled={invoice.previous}
                        onChange={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    number: e.target.value,
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableCurrencyInput
                        type="text"
                        labelText="Vlr. Total NF"
                        upperCaseLabel
                        defaultValue={invoice.gross_price}
                        disabled={invoice.previous}
                        onSet={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    gross_price: getCurrencyFormatValue(
                                      e.target.value,
                                    ),
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableCurrencyInput
                        type="text"
                        labelText="Vlr. Total Desc."
                        upperCaseLabel
                        defaultValue={invoice.discount_price}
                        disabled={invoice.previous}
                        onSet={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    discount_price: getCurrencyFormatValue(
                                      e.target.value,
                                    ),
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableDefaultInput
                        type="text"
                        labelText="Série Duplicata"
                        upperCaseLabel
                        defaultValue={invoice.title_series}
                        maxLength={3}
                        disabled={invoice.previous}
                        onChange={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    title_series: e.target.value,
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableDefaultInput
                        type="text"
                        labelText="Número Duplicata"
                        upperCaseLabel
                        defaultValue={invoice.title_number}
                        maxLength={10}
                        disabled={invoice.previous}
                        onChange={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    title_number: e.target.value,
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>

                    <Wrapper style={{ width: 200 }}>
                      <TableDefaultInput
                        type="text"
                        labelText="Parcela Duplicata"
                        upperCaseLabel
                        defaultValue={invoice.title_installment}
                        maxLength={2}
                        disabled={invoice.previous}
                        onChange={e => {
                          setInvoices(prevInvoices =>
                            prevInvoices.map(prevInv =>
                              prevInv.sequence === invoice.sequence
                                ? {
                                    ...prevInv,
                                    title_installment: e.target.value,
                                    internal_invoice_error: '',
                                    external_invoice_error: '',
                                    empty_fields_error: '',
                                    price_error: '',
                                    validation_status: 'pending',
                                  }
                                : { ...prevInv },
                            ),
                          );
                        }}
                      />
                    </Wrapper>
                  </div>

                  <div
                    style={{
                      marginTop: '1rem',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <ValidateAltButton
                      status={invoice.validation_status}
                      onClick={() => validateInvoice(invoice)}
                    />
                  </div>

                  {invoice.issue_date_error !== '' && (
                    <Error>{invoice.issue_date_error}</Error>
                  )}

                  {invoice.internal_invoice_error !== '' && (
                    <Error>{invoice.internal_invoice_error}</Error>
                  )}

                  {invoice.external_invoice_error !== '' && (
                    <Error>{invoice.external_invoice_error}</Error>
                  )}

                  {invoice.empty_fields_error !== '' && (
                    <Error>{invoice.empty_fields_error}</Error>
                  )}

                  {invoice.price_error !== '' && (
                    <Error>{invoice.price_error}</Error>
                  )}

                  {invoice.sequence === lastSequence.current && (
                    <div
                      style={{
                        margin: '1rem 0 0 0.5rem',
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <DeleteRowButton
                        type="button"
                        onClick={() => {
                          lastSequence.current -= 1;

                          if (invoice.previous) {
                            setInvoices(prevInvoices =>
                              prevInvoices.map(prevInv =>
                                prevInv.sequence === invoice.sequence
                                  ? {
                                      ...prevInv,
                                      deleted: true,
                                    }
                                  : { ...prevInv },
                              ),
                            );
                          } else {
                            setInvoices(prevInvoices =>
                              prevInvoices.filter(
                                prevInv =>
                                  prevInv.sequence !== invoice.sequence,
                              ),
                            );
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : (
          <div
            style={{ maxWidth: 680, display: 'flex', justifyContent: 'center' }}
          >
            <PropagateLoader color="#276c8d" />
          </div>
        )}

        <div
          style={{
            maxWidth: 680,
            marginTop: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <AddRowButton
            type="button"
            onClick={() => {
              lastSequence.current += 1;

              setInvoices(prevInvoices => [
                ...prevInvoices,
                {
                  issue_date: new Date(new Date().setHours(0, 0, 0, 0)),
                  series: '',
                  number: '',
                  gross_price: 0,
                  discount_price: 0,
                  title_series: '',
                  title_number: '',
                  title_installment: '',
                  sequence: lastSequence.current,
                  project_id: state.id,
                  user_inclusion_id: user.id,
                  user_update_id: user.id,
                  previous: false,
                  deleted: false,
                  issue_date_error: '',
                  internal_invoice_error: '',
                  external_invoice_error: '',
                  empty_fields_error: '',
                  price_error: '',
                  validation_status: 'pending',
                },
              ]);
            }}
          />
        </div>

        <Content>
          <Wrapper>
            <TextArea
              name="record_note"
              labelFor="record_note"
              labelText="Observação"
              error={errors?.record_note?.message}
              ref={register}
              maxLength={1000}
            />
          </Wrapper>
        </Content>

        <Content>
          <UploadFiles
            type="anexo"
            project_temp_id={projectTemp.current}
            project_type="REBATE"
            sequence={5}
            user_id={user.id}
          />
        </Content>

        <div
          style={{
            marginTop: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              totalDiscountPrice.current = invoices
                ? invoices
                    .filter(i => !i.deleted)
                    .reduce((acc, inv) => acc + inv.discount_price, 0)
                : 0;

              const validInvoices = invoices.filter(i => !i.deleted);

              if (validInvoices.length === 0) {
                setNotice({
                  title: 'Notas Fiscais Inválidas!',
                  subtitle: 'Necessário informar notas fiscais!',
                });

                setIsOpenNotice(true);
              } else if (totalDiscountPrice.current > state.price) {
                setNotice({
                  title: 'Valor Total de Descontos Inválido!',
                  subtitle:
                    'O valor total de descontos informados não ' +
                    'pode ser superior ao valor da solicitação!',
                });

                setIsOpenNotice(true);
              } else if (
                validInvoices.filter(
                  i =>
                    i.validation_status === 'error' ||
                    i.validation_status === 'pending',
                ).length > 0
              ) {
                setNotice({
                  title: 'Notas Fiscais Não Validadas!',
                  subtitle: 'Necessário validar notas fiscais!',
                });

                setIsOpenNotice(true);
              } else {
                setIsOpenConfirmProcedure(true);
              }
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title={`Confirma gravação da solicitação ${state.code}?`}
        subTitle={
          totalDiscountPrice.current >= state.price
            ? 'A solicitação será finalizada'
            : 'A solicitação será atualizada'
        }
        opConfirm="Sim"
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />

      <NoticeDialog
        isOpen={isOpenNotice}
        type="error"
        title={notice.title}
        subTitle={notice.subtitle}
        setIsOpen={setIsOpenNotice}
      />
    </Container>
  );
};

export default Procedure5;
